// Pure, gedeelde dag-precisie datum- + duur-helpers (geen datum-library).
//
// Bestandsnaam is historisch (ontstaan voor de inmiddels geretireerde DagTaak-
// module); de helpers zijn generiek en worden o.a. door de operationele
// takenmodule, werkbonnen en opleidingen gebruikt. Twee groepen: dag-precisie
// datum-conversie (UTC-midnight van de NL-kalenderdag, DST-veilig) +
// duur-aggregatie/format. Alles via Intl.DateTimeFormat + Date, conform CLAUDE.md.
//
// Datum-conventie: `datum`/`origineleDatum` worden opgeslagen als
// Date.UTC(y,m,d) waarbij y/m/d de Europe/Amsterdam-kalenderdatum is. Over de
// API gaan ze als 'yyyy-mm-dd' strings.

import { isWerkdag } from './uren-calculations'

// ─── Datum ────────────────────────────────────────────────────────────────────

/**
 * Parse een 'yyyy-mm-dd' string naar UTC-midnight (Date.UTC(y, m-1, d)).
 *
 * Gooit bij een ongeldig formaat of een niet-bestaande kalenderdatum
 * (bv. '2026-02-30'), zodat een typefout niet stilletjes naar een andere dag
 * rolt.
 */
export function parseDagDatum(s: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim())
  if (!m) {
    throw new Error(`Ongeldige datum: "${s}". Verwacht "yyyy-mm-dd".`)
  }
  const year = parseInt(m[1], 10)
  const month = parseInt(m[2], 10)
  const day = parseInt(m[3], 10)
  const d = new Date(Date.UTC(year, month - 1, day))
  // Controleer op overflow (bv. 31 feb → 2/3 maart): roundtrip moet kloppen.
  if (
    d.getUTCFullYear() !== year ||
    d.getUTCMonth() !== month - 1 ||
    d.getUTCDate() !== day
  ) {
    throw new Error(`Ongeldige datum: "${s}" bestaat niet in de kalender.`)
  }
  return d
}

/** Format een Date naar 'yyyy-mm-dd' op basis van de UTC-componenten. */
export function formatDagDatum(d: Date): string {
  const year = d.getUTCFullYear()
  const month = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * De NL-kalenderdag van "vandaag" als UTC-midnight.
 *
 * Leidt de Europe/Amsterdam-datum af (DST-veilig, niet uit process.tz) en mapt
 * die op Date.UTC(y,m,d). `now` is injecteerbaar voor deterministische tests.
 */
export function vandaagAmsterdam(now: Date = new Date()): Date {
  // 'en-CA' geeft het ISO-formaat 'yyyy-mm-dd' terug.
  const ymd = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)
  return parseDagDatum(ymd)
}

/**
 * Eerstvolgende werkdag (ma-vr, geen feestdag) STRIKT ná `datum`.
 *
 * `feestdagen` is een lijst Date's (UTC-midnight); een dag telt als feestdag
 * als de UTC-kalenderdatum overeenkomt. Hergebruikt `isWerkdag` uit
 * @streep/utils zodat de werkdag-definitie één bron heeft.
 */
export function nextWerkdag(datum: Date, feestdagen: Date[] = []): Date {
  // Start op de eerstvolgende dag (UTC-midnight) en loop vooruit tot een werkdag.
  const d = new Date(
    Date.UTC(datum.getUTCFullYear(), datum.getUTCMonth(), datum.getUTCDate() + 1),
  )
  // Bovengrens als veiligheidsklep (max ~2 weken vooruit dekt elke feestdag-cluster).
  for (let i = 0; i < 31; i++) {
    if (isWerkdag(d, feestdagen)) return d
    d.setUTCDate(d.getUTCDate() + 1)
  }
  // Onbereikbaar in de praktijk; geef de laatst-onderzochte dag terug.
  return d
}

// ─── Duur ─────────────────────────────────────────────────────────────────────

/**
 * Totale geschatte duur in minuten over een lijst taken. null/ontbrekend telt
 * als 0. Gebruikt voor het dag- en weektotaal in de backoffice.
 */
export function sumGeschatteDuurMin(
  taken: ReadonlyArray<{ geschatteDuurMin: number | null }>,
): number {
  let total = 0
  for (const t of taken) {
    if (typeof t.geschatteDuurMin === 'number' && Number.isFinite(t.geschatteDuurMin)) {
      total += t.geschatteDuurMin
    }
  }
  return total
}

/**
 * Korte, leesbare weergave van een duur in minuten.
 *   0    → '0m'
 *   45   → '45m'
 *   300  → '5u'
 *   270  → '4u 30m'
 * Negatieve invoer wordt op 0 geklemd.
 */
export function formatDuurKort(min: number): string {
  const total = Math.max(0, Math.round(min))
  const uren = Math.floor(total / 60)
  const minuten = total % 60
  if (uren === 0) return `${minuten}m`
  if (minuten === 0) return `${uren}u`
  return `${uren}u ${minuten}m`
}
